import { gql } from '@apollo/client';

/**
 * Account fragments live here rather than in fragments.tsx, which mixes Gatsby's
 * build-time `graphql` tag with Apollo's `gql`. These are runtime-only, and keeping
 * them out of that file keeps Gatsby's fragment extractor from parsing them.
 *
 * Field names verified against the live schema.
 */

export const CUSTOMER_ADDRESS_FRAGMENT = gql`
  fragment CustomerAddressFields on CustomerAddress {
    firstName
    lastName
    company
    address1
    address2
    city
    state
    postcode
    country
    email
    phone
  }
`;

export const CUSTOMER_FRAGMENT = gql`
  ${CUSTOMER_ADDRESS_FRAGMENT}
  fragment CustomerFields on Customer {
    id
    databaseId
    email
    username
    firstName
    lastName
    displayName
    role
    orderCount
    # Holds the wishlist's saved product ids (see WISHLIST.META_KEY /
    # useWishlist) — there is no dedicated wishlist field on this backend.
    metaData {
      key
      value
    }
    billing {
      ...CustomerAddressFields
    }
    shipping {
      ...CustomerAddressFields
    }
  }
`;

export const ORDER_SUMMARY_FRAGMENT = gql`
  fragment OrderSummaryFields on Order {
    id
    databaseId
    orderNumber
    date
    status
    # NOT format: RAW — Order money fields return null for RAW on this backend and
    # only yield a value when FORMATTED (the default). Cart is the opposite, which is
    # what made these totals come back empty. formatPrice decodes the entities.
    total
    # Order has no itemCount field; the count is the sum of these quantities.
    lineItems {
      nodes {
        quantity
      }
    }
  }
`;

/**
 * NOTE: `LineItem.name` is deliberately absent.
 *
 * The field does not exist in the schema — it only appears once the mu-plugin from
 * Phase 0.3 registers it. Querying a field that does not exist is a hard GraphQL
 * validation error, so including it early would break every order query rather than
 * degrade. Once the mu-plugin is deployed, add `name` here; `lineItemName()` in
 * src/utils/account/orderHelpers.ts already prefers it when present.
 */
export const LINE_ITEM_FRAGMENT = gql`
  fragment LineItemFields on LineItem {
    databaseId
    quantity
    subtotal
    subtotalTax
    total
    # Line amounts are exclusive of VAT; the tax is carried separately, so the
    # price a customer actually paid is total + totalTax.
    totalTax
    product {
      node {
        databaseId
        name
        uri
        ... on SimpleProduct {
          stockStatus
          image {
            sourceUrl(size: WOOCOMMERCE_THUMBNAIL)
          }
        }
      }
    }
  }
`;

export const ORDER_DETAIL_FRAGMENT = gql`
  ${CUSTOMER_ADDRESS_FRAGMENT}
  ${LINE_ITEM_FRAGMENT}
  fragment OrderDetailFields on Order {
    id
    databaseId
    orderNumber
    date
    datePaid
    status
    currency
    # Default (FORMATTED). RAW returns null for these on this backend.
    subtotal
    discountTotal
    shippingTotal
    shippingTax
    # All VAT on the order. Adding it between the subtotal and shipping makes the
    # column sum to the total, including the case where shipping is taxed.
    totalTax
    cartTax
    total
    paymentMethodTitle
    customerNote
    # Used as a second check that the order really belongs to the signed-in customer.
    customer {
      databaseId
    }
    billing {
      ...CustomerAddressFields
    }
    shipping {
      ...CustomerAddressFields
    }
    couponLines {
      nodes {
        code
        discount
      }
    }
    shippingLines {
      nodes {
        methodTitle
        total
      }
    }
    # Used only for the rate shown in the VAT label, e.g. "PVM (21%)".
    taxLines {
      nodes {
        taxTotal
        taxRate {
          rate
        }
      }
    }
    lineItems {
      nodes {
        ...LineItemFields
      }
    }
  }
`;
